import React, { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { createUtilityApi } from '../../../apis/apis'
import type { Utility } from '../../../apis/types'
import { FieldTooltip } from './TariffFieldKey'
import { UTILITY_TYPE_HINTS } from './TariffFilterBar'

const inputClass =
    'block w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100'

const TYPE_OPTIONS: Array<{ value: 'IOU' | 'CCA' | 'ESP_DA'; label: string; tooltip: string }> = [
    { value: 'IOU', label: 'IOU', tooltip: UTILITY_TYPE_HINTS.IOU },
    { value: 'CCA', label: 'CCA', tooltip: UTILITY_TYPE_HINTS.CCA },
    { value: 'ESP_DA', label: 'DA', tooltip: UTILITY_TYPE_HINTS.ESP_DA },
]

type FieldErrors = {
    name?: string
    shortName?: string
}

export const CreateUtilityModal: React.FC<{
    open: boolean
    onClose: () => void
    onCreated: (utility: Utility) => void
    defaultType?: 'IOU' | 'CCA' | 'ESP_DA'
}> = ({ open, onClose, onCreated, defaultType = 'IOU' }) => {
    const [type, setType] = useState<'IOU' | 'CCA' | 'ESP_DA'>(defaultType)
    const [name, setName] = useState('')
    const [shortName, setShortName] = useState('')
    const [loginUrl, setLoginUrl] = useState('')
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

    const resetAndClose = (): void => {
        setType(defaultType)
        setName('')
        setShortName('')
        setLoginUrl('')
        setError(null)
        setFieldErrors({})
        onClose()
    }

    const validate = (): boolean => {
        const next: FieldErrors = {}
        if (name.trim() === '') next.name = 'Name is required.'
        if (shortName.trim() === '') next.shortName = 'Short name is required.'
        setFieldErrors(next)
        return Object.keys(next).length === 0
    }

    const handleCreate = (event?: React.FormEvent): void => {
        event?.preventDefault()
        if (!validate()) return
        setSaving(true)
        setError(null)
        void createUtilityApi({
            name: name.trim(),
            shortName: shortName.trim(),
            type,
            ...(loginUrl.trim() !== '' ? { loginUrl: loginUrl.trim() } : {}),
        })
            .then((created) => {
                onCreated({ ...created, type })
                resetAndClose()
            })
            .catch((err: { response?: { status?: number; data?: { message?: string } } }) => {
                const message = err.response?.data?.message
                setError(
                    typeof message === 'string' && message !== ''
                        ? message
                        : 'Failed to create utility.'
                )
            })
            .finally(() => setSaving(false))
    }

    return (
        <Transition show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={resetAndClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/50 dark:bg-black/70" />
                </Transition.Child>
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-4 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-5 text-left shadow-xl dark:bg-slate-900">
                                <Dialog.Title className="text-lg font-medium text-slate-900 dark:text-slate-100">
                                    Create utility
                                </Dialog.Title>
                                <form className="mt-4 space-y-3" onSubmit={handleCreate} noValidate>
                                    <div>
                                        <div className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                            Utility type
                                        </div>
                                        <div
                                            className="inline-flex rounded-md shadow-sm"
                                            role="group"
                                            aria-label="Utility type"
                                        >
                                            {TYPE_OPTIONS.map((opt, index) => (
                                                <FieldTooltip key={opt.value} content={opt.tooltip}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setType(opt.value)}
                                                        className={[
                                                            'border px-3 py-1.5 text-sm font-medium',
                                                            index === 0 ? 'rounded-l-md' : '-ml-px',
                                                            index === TYPE_OPTIONS.length - 1
                                                                ? 'rounded-r-md'
                                                                : '',
                                                            type === opt.value
                                                                ? 'border-indigo-600 bg-indigo-600 text-white'
                                                                : 'border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200',
                                                        ].join(' ')}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                </FieldTooltip>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="create-utility-name"
                                            className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
                                        >
                                            Name
                                        </label>
                                        <input
                                            id="create-utility-name"
                                            className={inputClass}
                                            value={name}
                                            onChange={(event) => setName(event.target.value)}
                                            placeholder="e.g. Pacific Gas and Electric"
                                            required
                                            aria-invalid={fieldErrors.name != null}
                                            aria-describedby={
                                                fieldErrors.name != null ? 'create-utility-name-error' : undefined
                                            }
                                        />
                                        {fieldErrors.name != null ? (
                                            <p id="create-utility-name-error" className="mt-1 text-xs text-red-600">
                                                {fieldErrors.name}
                                            </p>
                                        ) : null}
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="create-utility-short-name"
                                            className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
                                        >
                                            Short name
                                        </label>
                                        <input
                                            id="create-utility-short-name"
                                            className={inputClass}
                                            value={shortName}
                                            onChange={(event) => setShortName(event.target.value)}
                                            placeholder="e.g. PG&E"
                                            required
                                            aria-invalid={fieldErrors.shortName != null}
                                            aria-describedby={
                                                fieldErrors.shortName != null
                                                    ? 'create-utility-short-name-error'
                                                    : undefined
                                            }
                                        />
                                        {fieldErrors.shortName != null ? (
                                            <p
                                                id="create-utility-short-name-error"
                                                className="mt-1 text-xs text-red-600"
                                            >
                                                {fieldErrors.shortName}
                                            </p>
                                        ) : null}
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="create-utility-login-url"
                                            className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
                                        >
                                            Login URL
                                        </label>
                                        <input
                                            id="create-utility-login-url"
                                            className={inputClass}
                                            value={loginUrl}
                                            onChange={(event) => setLoginUrl(event.target.value)}
                                            placeholder="e.g. https://www.pge.com"
                                        />
                                    </div>
                                    {error != null ? (
                                        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                                    ) : null}
                                    <div className="mt-5 flex justify-end gap-2">
                                        <button
                                            type="button"
                                            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                                            onClick={resetAndClose}
                                            disabled={saving}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-60"
                                            disabled={saving}
                                        >
                                            {saving ? 'Creating…' : 'Create'}
                                        </button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}

export default CreateUtilityModal
